import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { env } from '@/config/env';

export interface AuthUser {
  id: string;
  phone: string;
  fullName: string | null;
  nationalId: string | null;
  isProfileComplete: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isHydrated: boolean;

  setSession: (params: { accessToken: string; refreshToken: string; user: AuthUser }) => void;
  setUser: (user: AuthUser) => void;
  /** Attempts a silent refresh; returns the new access token or null on failure. */
  refreshSession: () => Promise<string | null>;
  logout: () => void;
}

/**
 * Client/session state only — tokens + user identity. Server-owned data
 * (members, documents, etc.) lives in TanStack Query, never here.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isHydrated: false,

      setSession: ({ accessToken, refreshToken, user }) => set({ accessToken, refreshToken, user }),

      setUser: (user) => set({ user }),

      refreshSession: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return null;
        try {
          // Plain axios.post (no interceptors) — avoids recursive 401
          // handling that would occur if this went through the shared
          // apiClient instance (which calls back into this same store).
          const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
            `${env.apiBaseUrl}/auth/refresh`,
            { refreshToken },
          );
          set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
          return data.accessToken;
        } catch {
          return null;
        }
      },

      logout: () => set({ accessToken: null, refreshToken: null, user: null }),
    }),
    {
      name: 'smda-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) state.isHydrated = true;
      },
    },
  ),
);

export const isAuthenticated = () => Boolean(useAuthStore.getState().accessToken);
